import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  FileText,
  PlayCircle,
  CheckSquare,
  BarChart3,
  Settings,
  Sparkles,
  Plus,
  Search,
  Zap,
  Bot,
  Send,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface CommandBarProps {
  organizationId: string;
}

interface QuickAction {
  id: string;
  label: string;
  icon: any;
  action: () => void;
  shortcut?: string;
  category: "navigation" | "action" | "ai";
}

export function CommandBar({ organizationId }: CommandBarProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [recentSops, setRecentSops] = useState<any[]>([]);
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Global keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Fetch recent items when opened
  useEffect(() => {
    if (open && organizationId) {
      fetchRecentItems();
    }
  }, [open, organizationId]);

  const fetchRecentItems = async () => {
    const [sopsRes, runsRes] = await Promise.all([
      supabase
        .from("sop_templates")
        .select("id, title")
        .eq("organization_id", organizationId)
        .eq("status", "ACTIVE")
        .order("updated_at", { ascending: false })
        .limit(5),
      supabase
        .from("sop_runs")
        .select("id, name")
        .eq("organization_id", organizationId)
        .eq("status", "IN_PROGRESS")
        .order("created_at", { ascending: false })
        .limit(5),
    ]);

    setRecentSops(sopsRes.data || []);
    setRecentRuns(runsRes.data || []);
  };

  const handleAICommand = useCallback(async (command: string) => {
    setIsProcessing(true);
    setOpen(false);

    toast({
      title: "Processing command...",
      description: command,
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Determine intent from natural language
      const lowerCommand = command.toLowerCase();
      let functionName = "";
      let params: any = {};

      if (lowerCommand.includes("task") && (lowerCommand.includes("my") || lowerCommand.includes("today") || lowerCommand.includes("overdue"))) {
        functionName = "ritual.task.list";
        params = { due_filter: lowerCommand.includes("overdue") ? "overdue" : "today" };
      } else if (lowerCommand.includes("sop") && lowerCommand.includes("list")) {
        functionName = "ritual.sop.list";
      } else if (lowerCommand.includes("run") && lowerCommand.includes("list")) {
        functionName = "ritual.run.list";
      } else if (lowerCommand.includes("analytics") || lowerCommand.includes("summary") || lowerCommand.includes("stats")) {
        functionName = "ritual.analytics.summary";
      } else if (lowerCommand.includes("generate") || lowerCommand.includes("create sop") || lowerCommand.includes("ai")) {
        navigate("/app/setup");
        toast({ title: "Opening AI Setup", description: "Use the AI assistant to generate SOPs" });
        return;
      } else {
        // Default to task list
        functionName = "ritual.task.list";
      }

      const response = await supabase.functions.invoke("ritual-agent-api", {
        body: { function: functionName, params },
      });

      if (response.error) throw response.error;

      const result = response.data?.data;
      
      // Navigate based on result type
      if (functionName.includes("task")) {
        navigate("/app/tasks");
        toast({
          title: "Tasks loaded",
          description: `Found ${result?.tasks?.length || 0} tasks`,
        });
      } else if (functionName.includes("sop")) {
        navigate("/app/sops");
        toast({
          title: "SOPs loaded",
          description: `Found ${result?.sops?.length || 0} SOPs`,
        });
      } else if (functionName.includes("run")) {
        navigate("/app/runs");
        toast({
          title: "Runs loaded",
          description: `Found ${result?.runs?.length || 0} runs`,
        });
      } else if (functionName.includes("analytics")) {
        navigate("/app/analytics");
        toast({
          title: "Analytics ready",
          description: `${result?.active_runs || 0} active runs, ${result?.tasks_due_today || 0} tasks due today`,
        });
      }
    } catch (error: any) {
      console.error("Command error:", error);
      toast({
        title: "Command failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [navigate, toast]);

  const navigationActions: QuickAction[] = [
    {
      id: "dashboard",
      label: "Go to Dashboard",
      icon: LayoutDashboard,
      action: () => { navigate("/app/dashboard"); setOpen(false); },
      shortcut: "⌘D",
      category: "navigation",
    },
    {
      id: "tasks",
      label: "View My Tasks",
      icon: CheckSquare,
      action: () => { navigate("/app/tasks"); setOpen(false); },
      shortcut: "⌘T",
      category: "navigation",
    },
    {
      id: "runs",
      label: "View Runs",
      icon: PlayCircle,
      action: () => { navigate("/app/runs"); setOpen(false); },
      shortcut: "⌘R",
      category: "navigation",
    },
    {
      id: "sops",
      label: "View SOPs",
      icon: FileText,
      action: () => { navigate("/app/sops"); setOpen(false); },
      shortcut: "⌘S",
      category: "navigation",
    },
    {
      id: "analytics",
      label: "View Analytics",
      icon: BarChart3,
      action: () => { navigate("/app/analytics"); setOpen(false); },
      category: "navigation",
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      action: () => { navigate("/app/settings"); setOpen(false); },
      category: "navigation",
    },
  ];

  const quickActions: QuickAction[] = [
    {
      id: "new-run",
      label: "Start New Run",
      icon: Plus,
      action: () => { navigate("/app/runs?new=true"); setOpen(false); },
      category: "action",
    },
    {
      id: "new-sop",
      label: "Create New SOP",
      icon: Plus,
      action: () => { navigate("/app/sops?new=true"); setOpen(false); },
      category: "action",
    },
    {
      id: "ai-setup",
      label: "AI Setup Wizard",
      icon: Sparkles,
      action: () => { navigate("/app/setup"); setOpen(false); },
      category: "ai",
    },
  ];

  const isAIQuery = query.length > 10 || 
    query.toLowerCase().includes("show") || 
    query.toLowerCase().includes("find") || 
    query.toLowerCase().includes("get") ||
    query.toLowerCase().includes("what") ||
    query.toLowerCase().includes("list");

  return (
    <>
      {/* Trigger button for mobile */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors w-full"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search or ask anything...</span>
        <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput 
          placeholder="Type a command, search, or ask AI..." 
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>
            {isAIQuery ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <Bot className="h-8 w-8 text-primary" />
                <p className="text-sm text-muted-foreground">Press Enter to run AI command</p>
                <button
                  onClick={() => handleAICommand(query)}
                  disabled={isProcessing}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Zap className="h-4 w-4" />
                  {isProcessing ? "Processing..." : "Run Command"}
                </button>
              </div>
            ) : (
              <p className="text-muted-foreground">No results found.</p>
            )}
          </CommandEmpty>

          {query.length === 0 && (
            <>
              <CommandGroup heading="Quick Actions">
                {quickActions.map((action) => (
                  <CommandItem
                    key={action.id}
                    onSelect={action.action}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                      <action.icon className="h-4 w-4 text-primary" />
                    </div>
                    <span>{action.label}</span>
                    {action.category === "ai" && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        AI
                      </Badge>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandSeparator />

              <CommandGroup heading="Navigation">
                {navigationActions.map((action) => (
                  <CommandItem
                    key={action.id}
                    onSelect={action.action}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <action.icon className="h-4 w-4 text-muted-foreground" />
                    <span>{action.label}</span>
                    {action.shortcut && (
                      <kbd className="ml-auto text-xs text-muted-foreground">
                        {action.shortcut}
                      </kbd>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>

              {recentSops.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Recent SOPs">
                    {recentSops.map((sop) => (
                      <CommandItem
                        key={sop.id}
                        onSelect={() => {
                          navigate(`/app/sops/${sop.id}`);
                          setOpen(false);
                        }}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{sop.title}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {recentRuns.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Active Runs">
                    {recentRuns.map((run) => (
                      <CommandItem
                        key={run.id}
                        onSelect={() => {
                          navigate(`/app/runs/${run.id}`);
                          setOpen(false);
                        }}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <PlayCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">{run.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </>
          )}

          {isAIQuery && query.length > 0 && (
            <CommandGroup heading="AI Assistant">
              <CommandItem
                onSelect={() => handleAICommand(query)}
                className="flex items-center gap-3 cursor-pointer"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Ask Ritual AI</p>
                  <p className="text-xs text-muted-foreground truncate">"{query}"</p>
                </div>
                <Send className="h-4 w-4 text-muted-foreground" />
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
